import { html, nothing } from "lit";
import {
  type CustomTheme,
  type CustomThemeSettings,
  type ThemePreset,
  THEME_PRESETS,
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_OPTIONS,
  DEFAULT_CUSTOM_THEME_SETTINGS,
  getPresetTheme,
  exportThemeToJson,
  importThemeFromJson,
} from "./theme.ts";

export interface ThemeEditorProps {
  settings: CustomThemeSettings;
  onSettingsChange: (next: CustomThemeSettings) => void;
}

// Render a color picker input
function renderColorPicker(label: string, value: string, onChange: (v: string) => void) {
  return html`
    <label class="field theme-editor__field">
      <span>${label}</span>
      <div class="theme-editor__color-row">
        <input
          type="color"
          class="theme-editor__color-input"
          .value=${value}
          @input=${(e: Event) => onChange((e.target as HTMLInputElement).value)}
        />
        <input
          type="text"
          class="theme-editor__color-text"
          .value=${value}
          @input=${(e: Event) => onChange((e.target as HTMLInputElement).value)}
          placeholder="#ff5c5c"
          pattern="^#[0-9a-fA-F]{6}$"
        />
      </div>
    </label>
  `;
}

// Render a select dropdown
function renderSelect(
  label: string,
  value: string,
  options: Array<{ value: string; label: string }>,
  onChange: (v: string) => void,
) {
  return html`
    <label class="field theme-editor__field">
      <span>${label}</span>
      <select
        class="theme-editor__select"
        .value=${value}
        @change=${(e: Event) => onChange((e.target as HTMLSelectElement).value)}
      >
        ${options.map(
          (opt) => html`
            <option value=${opt.value} ?selected=${value === opt.value}>
              ${opt.label}
            </option>
          `,
        )}
      </select>
    </label>
  `;
}

function renderPresetCard(
  preset: keyof typeof THEME_PRESETS,
  isSelected: boolean,
  onSelect: () => void,
) {
  const theme = THEME_PRESETS[preset];
  const presetLabels: Record<keyof typeof THEME_PRESETS, string> = {
    default: "Default Red",
    ocean: "Ocean Blue",
    forest: "Forest Green",
    sunset: "Sunset Orange",
    midnight: "Midnight Purple",
  };
  return html`
    <button
      class="theme-editor__preset-card ${isSelected ? "theme-editor__preset-card--selected" : ""}"
      @click=${onSelect}
      title=${presetLabels[preset]}
    >
      <div
        class="theme-editor__preset-swatch"
        style="background: ${theme.background}; border: 1px solid ${theme.border};"
      >
        <div
          class="theme-editor__preset-accent"
          style="background: ${theme.accent};"
        ></div>
      </div>
      <span class="theme-editor__preset-label">${presetLabels[preset]}</span>
    </button>
  `;
}

export function renderThemeEditor(props: ThemeEditorProps) {
  const { settings, onSettingsChange } = props;
  const isCustom = settings.preset === "custom";
  const activeTheme = getPresetTheme(settings.preset, settings.customTheme);

  const updatePreset = (preset: ThemePreset) => {
    onSettingsChange({ ...settings, preset });
  };

  const updateCustom = (partial: Partial<CustomTheme>) => {
    const customTheme = {
      ...(settings.customTheme ?? { ...THEME_PRESETS.default }),
      ...partial,
    };
    onSettingsChange({
      ...settings,
      preset: "custom",
      customTheme,
    });
  };

  const updateFontFamily = (fontFamily: string) => {
    onSettingsChange({ ...settings, fontFamily });
  };

  const updateFontSize = (fontSize: number) => {
    onSettingsChange({ ...settings, fontSize });
  };

  const handleExport = () => {
    const json = exportThemeToJson(settings);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "custom-theme.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,application/json";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const imported = importThemeFromJson(text);
        if (imported) {
          onSettingsChange(imported);
        } else {
          alert("Invalid theme file format");
        }
      } catch {
        alert("Failed to read theme file");
      }
    };
    input.click();
  };

  return html`
    <div class="theme-editor">
      <!-- Theme Presets -->
      <section class="theme-editor__section">
        <h3 class="theme-editor__section-title">Theme Presets</h3>
        <div class="theme-editor__presets">
          ${(Object.keys(THEME_PRESETS) as Array<keyof typeof THEME_PRESETS>).map((preset) =>
            renderPresetCard(preset, settings.preset === preset, () => updatePreset(preset)),
          )}
        </div>
      </section>

      <!-- Accent Color Picker -->
      <section class="theme-editor__section">
        <h3 class="theme-editor__section-title">Accent Color</h3>
        <p class="theme-editor__hint">
          Choose a preset accent or pick a custom color below.
        </p>
        <!-- Accent Color Palette -->
        <div class="theme-editor__accent-palette">
          ${ACCENT_PALETTE.map((color) => {
            const hsl = hexToHsl(color);
            const isSelected = activeTheme.accent.toLowerCase() === color.toLowerCase();
            return html`
              <button
                type="button"
                class="theme-editor__accent-swatch ${isSelected ? "theme-editor__accent-swatch--selected" : ""}"
                style="background: ${color};"
                title=${color}
                aria-label=${`Select accent color ${color}`}
                aria-pressed=${isSelected}
                @click=${() => {
                  updateCustom({
                    accent: color,
                    accentHover: hslLighten(hsl, 10),
                    accentMuted: color,
                    accentSubtle: `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, 0.15)`,
                    accentForeground: "#fafafa",
                    accentGlow: `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, 0.25)`,
                  });
                }}
              ></button>
            `;
          })}
        </div>
        <!-- Custom Color Picker -->
        <div class="theme-editor__colors">
          ${renderColorPicker("Custom Accent", activeTheme.accent, (v) => {
            const hsl = hexToHsl(v);
            updateCustom({
              accent: v,
              accentHover: hslLighten(hsl, 10),
              accentMuted: v,
              accentSubtle: `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, 0.15)`,
              accentForeground: "#fafafa",
              accentGlow: `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, 0.25)`,
            });
          })}
        </div>
      </section>

      <!-- Font Settings -->
      <section class="theme-editor__section">
        <h3 class="theme-editor__section-title">Typography</h3>
        <div class="theme-editor__font-controls">
          ${renderSelect("Font Family", settings.fontFamily, FONT_FAMILY_OPTIONS, updateFontFamily)}
          ${renderSelect(
            "Font Size",
            String(settings.fontSize),
            FONT_SIZE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label })),
            (v) => updateFontSize(Number(v)),
          )}
        </div>
      </section>

      <!-- Custom Theme (Advanced) -->
      ${
        isCustom
          ? html`
        <section class="theme-editor__section">
          <h3 class="theme-editor__section-title">Custom Colors</h3>
          <p class="theme-editor__hint">
            Fine-tune individual colors for your custom theme.
          </p>
          <div class="theme-editor__custom-colors">
            ${renderColorPicker("Background", activeTheme.background, (v) => updateCustom({ background: v }))}
            ${renderColorPicker("Card", activeTheme.card, (v) => updateCustom({ card: v }))}
            ${renderColorPicker("Text", activeTheme.text, (v) => updateCustom({ text: v }))}
            ${renderColorPicker("Muted Text", activeTheme.muted, (v) => updateCustom({ muted: v }))}
            ${renderColorPicker("Border", activeTheme.border, (v) => updateCustom({ border: v }))}
            ${renderColorPicker("Elevated Surface", activeTheme.backgroundElevated, (v) => updateCustom({ backgroundElevated: v }))}
          </div>
        </section>
      `
          : nothing
      }

      <!-- Import/Export -->
      <section class="theme-editor__section">
        <h3 class="theme-editor__section-title">Import / Export</h3>
        <div class="theme-editor__actions">
          <button class="btn" @click=${handleExport}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export Theme
          </button>
          <button class="btn" @click=${handleImport}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Import Theme
          </button>
        </div>
      </section>
    </div>
  `;
}

// ─── Color Utilities ─────────────────────────────────────────────────────────

/** Predefined accent color palette for quick selection. */
export const ACCENT_PALETTE = [
  // Reds
  "#ff5c5c", // Default Red
  "#ef4444", // Red 500
  "#dc2626", // Red 600
  // Oranges
  "#f97316", // Orange 500 (Sunset)
  "#ea580c", // Orange 600
  // Amber/Yellow
  "#f59e0b", // Amber 500
  "#d97706", // Amber 600
  // Greens
  "#22c55e", // Green 500 (Forest)
  "#16a34a", // Green 600
  "#10b981", // Emerald 500
  // Teals/Cyans
  "#14b8a6", // Teal 500
  "#06b6d4", // Cyan 500
  // Blues
  "#3b82f6", // Blue 500
  "#2563eb", // Blue 600
  "#00b4d8", // Ocean Blue
  // Indigo/Violet
  "#6366f1", // Indigo 500
  "#8b5cf6", // Violet 500
  "#a855f7", // Purple 500 (Midnight)
  "#d946ef", // Fuchsia 500
  // Pinks
  "#ec4899", // Pink 500
  "#f43f5e", // Rose 500
];

interface HSL {
  h: number;
  s: number;
  l: number;
}

function hexToHsl(hex: string): HSL {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return { h: 0, s: 0, l: 0 };
  }
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslLighten(hsl: HSL, percent: number): string {
  const newL = Math.min(100, hsl.l + percent);
  return hslToHex(hsl.h, hsl.s, newL);
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

export const THEME_EDITOR_STYLES = `
.theme-editor {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
}

.theme-editor__section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.theme-editor__section-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-strong, #fafafa);
}

.theme-editor__hint {
  margin: 0;
  font-size: 12px;
  color: var(--muted, #71717a);
}

/* Preset Cards */
.theme-editor__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.theme-editor__preset-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px;
  border: 2px solid var(--border, #27272a);
  border-radius: 8px;
  background: var(--card, #181b22);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.theme-editor__preset-card:hover {
  border-color: var(--accent, #ff5c5c);
}

.theme-editor__preset-card--selected {
  border-color: var(--accent, #ff5c5c);
  box-shadow: 0 0 0 2px var(--accent-glow, rgba(255, 92, 92, 0.25));
}

.theme-editor__preset-swatch {
  width: 48px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  overflow: hidden;
}

.theme-editor__preset-accent {
  flex: 1;
}

.theme-editor__preset-label {
  font-size: 10px;
  color: var(--text, #e4e4e7);
}

/* Accent Color Palette */
.theme-editor__accent-palette {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.theme-editor__accent-swatch {
  width: 28px;
  height: 28px;
  border: 2px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.1s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.theme-editor__accent-swatch:hover {
  transform: scale(1.1);
  border-color: var(--border, #27272a);
}

.theme-editor__accent-swatch:focus-visible {
  outline: 2px solid var(--accent, #ff5c5c);
  outline-offset: 2px;
}

.theme-editor__accent-swatch--selected {
  border-color: var(--text-strong, #fafafa);
  box-shadow: 0 0 0 2px var(--bg, #12141a), 0 0 0 4px var(--accent, #ff5c5c);
}

/* Color Controls */
.theme-editor__colors {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.theme-editor__color-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.theme-editor__color-input {
  width: 40px;
  height: 32px;
  padding: 2px;
  border: 1px solid var(--border, #27272a);
  border-radius: 4px;
  background: var(--card, #181b22);
  cursor: pointer;
}

.theme-editor__color-input::-webkit-color-swatch-wrapper {
  padding: 0;
}

.theme-editor__color-input::-webkit-color-swatch {
  border: none;
  border-radius: 2px;
}

.theme-editor__color-text {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid var(--border, #27272a);
  border-radius: 4px;
  background: var(--bg, #12141a);
  color: var(--text, #e4e4e7);
  font-family: var(--mono, monospace);
  font-size: 12px;
}

/* Font Controls */
.theme-editor__font-controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Custom Colors */
.theme-editor__custom-colors {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}

/* Actions */
.theme-editor__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.theme-editor__actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* Field Styles */
.theme-editor__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.theme-editor__field > span {
  font-size: 12px;
  color: var(--muted, #71717a);
}

.theme-editor__select {
  padding: 6px 8px;
  border: 1px solid var(--border, #27272a);
  border-radius: 4px;
  background: var(--bg, #12141a);
  color: var(--text, #e4e4e7);
  font-size: 13px;
  cursor: pointer;
}

.theme-editor__select:focus {
  outline: none;
  border-color: var(--accent, #ff5c5c);
}
`;
